import React, { useEffect, useMemo, useState } from "react";
import { BookOpen, ExternalLink, Library, ScrollText } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import ScriptureHubNav from "../components/ScriptureHubNav";
import { useAuth } from "../context/AuthContext";
import commandData from "../data/turahCommands.json";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function MyLibrary() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [library, setLibrary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate("/login?next=/my-library", { replace: true });
      return;
    }
    setLoading(true);
    axios.get(API + "/library", { withCredentials: true })
      .then((response) => setLibrary(response.data || { devotionals: [], command_ids: [] }))
      .catch(() => setLibrary({ devotionals: [], command_ids: [] }))
      .finally(() => setLoading(false));
  }, [user, authLoading, navigate]);

  const savedCommands = useMemo(() => {
    const ids = new Set(library?.command_ids || []);
    return commandData.commands.filter((command) => ids.has(command.id));
  }, [library]);

  if (authLoading || loading) {
    return <div className="min-h-[60vh] bg-parchment flex items-center justify-center text-slate-500">Loading your library…</div>;
  }

  if (!user) return null;

  const devotionals = library?.devotionals || [];

  return (
    <div className="min-h-screen bg-parchment" data-testid="my-scripture-library-page">
      <ScriptureHubNav />

      <section className="border-b-4 border-kingdom-gold bg-royal-blue text-white">
        <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 sm:py-10">
          <div className="flex items-center gap-3">
            <Library className="h-8 w-8 text-kingdom-gold" />
            <div>
              <p className="text-xs font-black uppercase tracking-[0.2em] text-kingdom-gold">Your saved study</p>
              <h1 className="mt-1 font-heading text-4xl sm:text-5xl">My Library</h1>
            </div>
          </div>
          <p className="mt-4 max-w-2xl text-white/80">
            Saved Scriptures, devotional portions, palal, videos, and Turah commands stay with this one account.
          </p>
        </div>
      </section>

      <main className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
        <div className="grid gap-8 lg:grid-cols-2">
          <section>
            <div className="mb-4 flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-kingdom-gold" />
              <h2 className="font-heading text-2xl text-royal-blue">Saved Daily Word & Devotional</h2>
            </div>

            {devotionals.length === 0 ? (
              <div className="border border-slate-200 bg-white p-6 text-slate-600">
                Nothing saved from the Daily Word yet.
                <Link to="/daily-devotional" className="ml-1 font-semibold text-royal-blue underline">Open the devotional.</Link>
              </div>
            ) : (
              <div className="space-y-3">
                {devotionals.map((item) => (
                  <Link
                    key={item.day_of_year}
                    to={"/daily-devotional/" + item.day_of_year}
                    className="block border border-slate-200 bg-white p-4 shadow-sm hover:border-kingdom-gold"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div>
                        <div className="text-xs font-bold uppercase tracking-wider text-slate-500">
                          Month {item.month} · Day {item.day}
                        </div>
                        <div className="mt-1 font-semibold text-royal-blue">{item.reference}</div>
                        <div className="mt-1 text-sm text-slate-600">{item.devotional_title || item.theme}</div>
                      </div>
                      <ExternalLink className="h-4 w-4 text-slate-400" />
                    </div>
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {(item.items || []).map((saved) => (
                        <span key={saved} className="rounded-full bg-royal-blue/10 px-2.5 py-1 text-[11px] font-bold uppercase text-royal-blue">
                          {saved}
                        </span>
                      ))}
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </section>

          <section>
            <div className="mb-4 flex items-center gap-2">
              <ScrollText className="h-5 w-5 text-kingdom-gold" />
              <h2 className="font-heading text-2xl text-royal-blue">Saved Turah Commands</h2>
            </div>

            {savedCommands.length === 0 ? (
              <div className="border border-slate-200 bg-white p-6 text-slate-600">
                No commands saved yet.
                <Link to="/turah-commands" className="ml-1 font-semibold text-royal-blue underline">Browse all 157 commands.</Link>
              </div>
            ) : (
              <div className="space-y-3">
                {savedCommands.map((command) => (
                  <div key={command.id} className="border border-slate-200 bg-white p-4 shadow-sm">
                    <div className="flex items-start gap-3">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-kingdom-gold font-bold text-royal-blue">
                        {command.id}
                      </span>
                      <div className="min-w-0">
                        <div className="font-semibold text-slate-800">{command.title}</div>
                        <div className="mt-3 flex flex-wrap gap-2">
                          {command.references.map((ref, index) => (
                            ref.url ? (
                              <a
                                key={ref.reference + index}
                                href={ref.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 rounded border border-kingdom-gold/30 px-2.5 py-1.5 text-xs font-semibold text-royal-blue"
                              >
                                {ref.reference} <ExternalLink className="h-3 w-3" />
                              </a>
                            ) : (
                              <span key={ref.reference + index} className="text-xs text-slate-600">{ref.reference}</span>
                            )
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      </main>
    </div>
  );
}
